import { HeartOutlined, PlusOutlined } from "@ant-design/icons";
import { App, Button, Pagination, Select, Spin, Table } from "antd";
import { useState } from "react";
import { DataTableCard, EmptyState, PageHeader, RowActions } from "@/shared/components/ui";
import { useTableState } from "@/shared/hooks/useTableState";
import { formatDateTime, orDash } from "@/shared/lib/format";
import { AidRecordFormModal } from "../components/AidRecordFormModal";
import { StatusTag, statusOptions } from "../components/StatusTag";
import {
	useAidRecordsList,
	useCreateAidRecord,
	useDeleteAidRecord,
	useUpdateAidRecord,
} from "../hooks/useAidRecords";
import type { AidRecord, AidRecordStatus } from "../types";

interface AidRecordsMobileListProps {
	records: AidRecord[];
	isLoading: boolean;
	status?: AidRecordStatus;
	total: number;
	page: number;
	limit: number;
	onPageChange: (page: number, pageSize: number) => void;
	openCreate: () => void;
	openEdit: (record: AidRecord) => void;
	onDelete: (id: string) => void;
	deleting: boolean;
}

function AidRecordsMobileList({
	records,
	isLoading,
	status,
	total,
	page,
	limit,
	onPageChange,
	openCreate,
	openEdit,
	onDelete,
	deleting,
}: AidRecordsMobileListProps) {
	if (isLoading) {
		return (
			<div className="flex justify-center p-8">
				<Spin />
			</div>
		);
	}

	if (records.length === 0) {
		return (
			<div className="p-4">
				<EmptyState
					title={status ? "Bu holatda yozuv yo'q" : "Yordam yozuvlari yo'q"}
					description={
						status
							? "Boshqa holatni tanlab ko'ring."
							: "Tarqatilgan yordamlarni shu yerda qayd qilasiz."
					}
					icon={<HeartOutlined />}
					action={
						status ? null : (
							<Button icon={<PlusOutlined />} onClick={openCreate}>
								Yordam qo'shish
							</Button>
						)
					}
				/>
			</div>
		);
	}

	return (
		<div>
			<div className="divide-y divide-[var(--c-border-subtle)]">
				{records.map((record) => (
					<div key={record.id} className="space-y-2 p-3.5">
						<div className="flex items-start justify-between gap-2">
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm font-medium text-[var(--c-text)]">
									{record.person.name}
								</div>
								<div className="numeric mt-0.5 text-xs text-[var(--c-text-muted)]">
									{formatDateTime(record.givenAt)}
								</div>
							</div>
							<div className="flex shrink-0 items-center gap-1.5">
								<StatusTag status={record.status} />
								<RowActions
									alwaysVisible
									onEdit={() => openEdit(record)}
									onDelete={() => onDelete(record.id)}
									deleteTitle="Yozuv o'chirilsinmi?"
									deleting={deleting}
								/>
							</div>
						</div>

						<div className="flex items-center justify-between rounded-md border border-[var(--c-border-subtle)] bg-[var(--c-canvas)] px-2.5 py-1.5 text-xs">
							<div className="min-w-0 truncate pr-2">
								<span className="text-[var(--c-text-muted)]">Mahsulot: </span>
								<span className="font-medium text-[var(--c-text)]">{record.item.name}</span>
							</div>
							<span className="numeric shrink-0 rounded bg-[var(--c-accent-soft)] px-2 py-0.5 font-semibold text-[var(--c-accent)]">
								{record.quantity} dona
							</span>
						</div>

						<div className="flex items-center justify-between text-[11px] text-[var(--c-text-muted)]">
							<span>
								Xodim:{" "}
								<span className="text-[var(--c-text-secondary)]">
									{orDash(record.givenBy.name)}
								</span>
							</span>
							{record.procedure && (
								<span>
									Tartib: <span className="text-[var(--c-text-secondary)]">{record.procedure}</span>
								</span>
							)}
						</div>
					</div>
				))}
			</div>

			{total > limit && (
				<div className="flex justify-center border-t border-[var(--c-border-subtle)] p-3">
					<Pagination
						simple
						current={page}
						pageSize={limit}
						total={total}
						onChange={onPageChange}
					/>
				</div>
			)}
		</div>
	);
}

export function AidRecordsPage() {
	const { message } = App.useApp();
	const { page, limit, onPageChange, resetPage } = useTableState();
	const [status, setStatus] = useState<AidRecordStatus | undefined>();
	const { data, isLoading, isFetching } = useAidRecordsList({ page, limit, status });

	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState<AidRecord | null>(null);

	const createMutation = useCreateAidRecord();
	const updateMutation = useUpdateAidRecord();
	const deleteMutation = useDeleteAidRecord();

	const openCreate = () => {
		setEditing(null);
		setModalOpen(true);
	};

	const openEdit = (record: AidRecord) => {
		setEditing(record);
		setModalOpen(true);
	};

	const handleStatusChange = (next: AidRecordStatus | undefined) => {
		setStatus(next);
		resetPage();
	};

	const handleSubmit = (values: Parameters<typeof createMutation.mutate>[0]) => {
		if (editing) {
			updateMutation.mutate(
				{ id: editing.id, data: values },
				{ onSuccess: () => setModalOpen(false) }
			);
		} else {
			createMutation.mutate(values, {
				onSuccess: () => {
					setModalOpen(false);
					// The backend always creates records as "approved" — if a Pending/
					// Rejected filter is active, the new record would be invisible on
					// the very page that just reported success. Clear it so the record
					// the user just created is guaranteed to be on screen.
					if (status !== undefined) {
						setStatus(undefined);
					}
					resetPage();
				},
			});
		}
	};

	const handleDelete = (id: string) => {
		deleteMutation.mutate(id, {
			onError: () => message.error("Yozuvni o'chirib bo'lmadi"),
		});
	};

	const total = data?.meta.total ?? 0;
	const records = data?.items ?? [];
	const isDataLoading = isLoading || isFetching;

	return (
		<div>
			<PageHeader
				title="Yordamlar"
				description="Berilgan yordamlar ro'yxati va holati"
				actions={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={openCreate}
						className="w-full sm:w-auto"
					>
						Yordam qo'shish
					</Button>
				}
			/>

			<DataTableCard
				toolbar={
					<Select
						placeholder="Holat"
						allowClear
						className="w-full sm:w-44"
						value={status}
						onChange={handleStatusChange}
						options={statusOptions}
					/>
				}
				toolbarExtra={total ? `${total} ta yozuv` : null}
			>
				{/* Desktop Table View */}
				<div className="hidden sm:block">
					<Table<AidRecord>
						rowKey="id"
						loading={isDataLoading}
						dataSource={records}
						scroll={{ x: "max-content" }}
						locale={{
							emptyText: (
								<EmptyState
									title={status ? "Bu holatda yozuv yo'q" : "Yordam yozuvlari yo'q"}
									description={
										status
											? "Boshqa holatni tanlab ko'ring."
											: "Tarqatilgan yordamlarni shu yerda qayd qilasiz."
									}
									icon={<HeartOutlined />}
									action={
										status ? null : (
											<Button icon={<PlusOutlined />} onClick={openCreate}>
												Yordam qo'shish
											</Button>
										)
									}
								/>
							),
						}}
						pagination={
							total > limit
								? {
										current: page,
										pageSize: limit,
										total,
										showSizeChanger: true,
										onChange: onPageChange,
										className: "!px-4",
									}
								: false
						}
						columns={[
							{
								title: "Berilgan vaqt",
								dataIndex: "givenAt",
								width: 160,
								render: (value: string) => <span className="numeric">{formatDateTime(value)}</span>,
							},
							{
								title: "Kimga",
								key: "person",
								render: (_, record) => <span className="font-medium">{record.person.name}</span>,
							},
							{
								title: "Mahsulot",
								key: "item",
								render: (_, record) => (
									<div className="min-w-0">
										<div className="truncate">{record.item.name}</div>
										<div className="numeric text-xs text-[var(--c-text-muted)]">
											{record.quantity} dona
										</div>
									</div>
								),
							},
							{
								title: "Kim berdi",
								key: "givenBy",
								responsive: ["lg"],
								render: (_, record) => (
									<span className="text-[var(--c-text-secondary)]">
										{orDash(record.givenBy.name)}
									</span>
								),
							},
							{
								title: "Tartib",
								dataIndex: "procedure",
								responsive: ["xl"],
								render: (value: string | null) => (
									<span className="text-[var(--c-text-secondary)]">{orDash(value)}</span>
								),
							},
							{
								title: "Holat",
								dataIndex: "status",
								width: 140,
								render: (value: AidRecordStatus) => <StatusTag status={value} />,
							},
							{
								title: "",
								key: "actions",
								width: 88,
								align: "right",
								fixed: "right",
								render: (_, record) => (
									<RowActions
										onEdit={() => openEdit(record)}
										onDelete={() => handleDelete(record.id)}
										deleteTitle="Yozuv o'chirilsinmi?"
										deleting={deleteMutation.isPending}
									/>
								),
							},
						]}
					/>
				</div>

				{/* Mobile Cards View */}
				<div className="block sm:hidden">
					<AidRecordsMobileList
						records={records}
						isLoading={isDataLoading}
						status={status}
						total={total}
						page={page}
						limit={limit}
						onPageChange={onPageChange}
						openCreate={openCreate}
						openEdit={openEdit}
						onDelete={handleDelete}
						deleting={deleteMutation.isPending}
					/>
				</div>
			</DataTableCard>

			<AidRecordFormModal
				open={modalOpen}
				editing={editing}
				loading={createMutation.isPending || updateMutation.isPending}
				onCancel={() => setModalOpen(false)}
				onSubmit={handleSubmit}
			/>
		</div>
	);
}
